import { z } from 'zod';

import { RatchetMetricType } from '@dealstack/shared/lib/generated';

const RatchetScheduleItemShape = z.object({
	threshold: z.preprocess(
		(val) => (val === '' || val === undefined ? undefined : Number(val)),
		z
			.number()
			.positive()
			.multipleOf(0.01, 'Threshold can have at most 2 decimal places')
			.optional()
	),
	xirrThreshold: z.preprocess(
		(val) => (val === '' || val === undefined ? undefined : Number(val)),
		z
			.number()
			.positive()
			.max(100)
			.multipleOf(0.01, 'Threshold can have at most 2 decimal places')
			.optional()
	),
	entitlement: z.preprocess(
		(val) => (val === '' || val === undefined ? undefined : Number(val)),
		z.number().min(0).max(100)
	),
});

// schedule[0] maps to initialEntitlementPercentage on the backend and schedule[1..N] to entries.
// Backend requires entries.length >= 1, non-null thresholds per entry (matching the metric type),
// and each entry entitlement > initial entitlement. All those rules land as one API 400 with no
// field targeting, so we duplicate them here to surface as per-row form errors.
export const RatchetingShape = z
	.object({
		metric: z.nativeEnum(RatchetMetricType),
		subjects: z.array(z.string()).min(1),
		schedule: z
			.array(RatchetScheduleItemShape)
			.min(2, 'Add at least one ratchet threshold below the initial row'),
		initialEntitlement: z.preprocess(
			(val) => (val === '' || val === undefined ? undefined : Number(val)),
			z.number().min(0).max(100)
		),
		distributeUntilBreakpoint: z.boolean(),
	})
	.superRefine((data, ctx) => {
		const [initial, ...entries] = data.schedule;
		if (!initial) {
			return;
		}
		const isCombined = data.metric === RatchetMetricType.MOIC_AND_XIRR;

		entries.forEach((entry, i) => {
			const idx = i + 1;
			if (entry.threshold === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['schedule', idx, 'threshold'],
					message:
						data.metric === RatchetMetricType.XIRR
							? 'XIRR threshold is required'
							: 'MOIC threshold is required',
				});
			}
			if (isCombined && entry.xirrThreshold === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['schedule', idx, 'xirrThreshold'],
					message: 'XIRR threshold is required',
				});
			}
			if (entry.entitlement <= initial.entitlement) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['schedule', idx, 'entitlement'],
					message: `Must be greater than the initial entitlement (${initial.entitlement}%)`,
				});
			}
		});
	});

export type RatchetScheduleItemFormDto = z.infer<typeof RatchetScheduleItemShape>;
export type RatchetingFormDto = z.infer<typeof RatchetingShape>;
